import { injectable, interfaces } from 'inversify';
import { action, computed, makeObservable, observable } from 'mobx';
import moment from 'moment-timezone';
import { TimePeriod } from '../../../../../design/selects/timePeriodSelector/S_TimePeriodSelector';

export interface IProjectStatsFiltersStore {
  availableWorkers: IWorker[];
  endDate: moment.Moment;
  hiddenWorkers: IWorker[];
  projectCreatedDate: moment.Moment;
  projectId: string;
  startDate: moment.Moment;
  timePeriod: TimePeriod;
  visibleWorkers: IWorker[];
  load: (projectId: string, projectCreatedDate: Date, workers: IWorker[], startDate: moment.Moment, endDate: moment.Moment) => void;
  setAvailableWorkers: (availableWorkers: IWorker[]) => void;
  setDates: (startDate: moment.Moment, endDate: moment.Moment) => void;
  setHiddenWorkers: (hiddenWorkers: IWorker[]) => void;
  setTimePeriod: (dateRangeType: TimePeriod) => void;
}

export namespace IProjectStatsFiltersStore {
  export const $: interfaces.ServiceIdentifier<IProjectStatsFiltersStore> = Symbol('PROJECT_STATS_FILTERS_STORE');
}

export interface IWorker {
  id: string;
  email: string;
}

@injectable()
export class ProjectStatsFiltersStore implements IProjectStatsFiltersStore {
  availableWorkers: IWorker[] = [];

  endDate: moment.Moment = moment();

  hiddenWorkers: IWorker[] = [];

  projectCreatedDate: moment.Moment = moment();

  projectId: string = '';

  startDate: moment.Moment = moment();

  timePeriod: TimePeriod = TimePeriod.Month;

  constructor() {
    makeObservable(this, {
      availableWorkers: observable,
      endDate: observable,
      hiddenWorkers: observable,
      projectCreatedDate: observable,
      projectId: observable,
      startDate: observable,
      timePeriod: observable,
      setHiddenWorkers: action,
      setAvailableWorkers: action,
      setTimePeriod: action,
      setDates: action,
      load: action,
      visibleWorkers: computed,
    });
  }

  setHiddenWorkers = (hiddenWorkers: IWorker[]): void => {
    this.hiddenWorkers = hiddenWorkers;
  };

  setAvailableWorkers = (availableWorkers: IWorker[]): void => {
    this.availableWorkers = availableWorkers;
  };

  setTimePeriod = (timePeriod: TimePeriod): void => {
    this.timePeriod = timePeriod;
  };

  setDates = (startDate: moment.Moment, endDate: moment.Moment): void => {
    this.startDate = startDate;
    this.endDate = endDate;
  };

  load = (projectId: string, projectCreatedDate: Date, workers: IWorker[], startDate: moment.Moment, endDate: moment.Moment): void => {
    this.projectId = projectId;
    this.availableWorkers = workers;
    this.hiddenWorkers = [];
    this.projectCreatedDate = moment(projectCreatedDate).utcOffset(0, false);
    this.timePeriod = TimePeriod.Month;
    this.startDate = startDate;
    this.endDate = endDate;
  };

  get visibleWorkers(): IWorker[] {
    return this.availableWorkers.filter((w) => !this.hiddenWorkers.map((hd) => hd.id).includes(w.id));
  }
}
